import React from 'react';
import type { WineColorCanonical } from '../../../utils/wineColor';
import { dotVarFor, formatPrice, type LaCarteWine } from './laCarteModel';

interface CardProps {
  wine: LaCarteWine;
  selected: boolean;
  picked: boolean;
  onOpen: (id: string) => void;
}

/**
 * One wine as a clearly-defined clickable card box (single column), reading
 * like a line on a printed carte: producer eyebrow, structured name in serif,
 * classification whispered beneath when the wine has one, then the meta line
 * (dot · vintage · country · appellation). The right column carries the
 * price and ONE quiet status: picked beats scarcity; sold-out dims the card.
 */
export const WineCard: React.FC<CardProps> = ({ wine, selected, picked, onOpen }) => {
  const metaBits = [wine.vintage ?? 'NV', wine.regionLine].filter(Boolean).join(' · ');
  const soldOut = wine.stockBand === 'SOLD_OUT';
  const status = picked
    ? { className: 'lc-picked', text: '✓ picked' }
    : wine.stockBand === 'LOW_STOCK'
      ? { className: 'lc-stock lc-stock--low', text: 'Last bottles' }
      : soldOut
        ? { className: 'lc-stock lc-stock--out', text: 'Sold out' }
        : { className: 'lc-picked', text: '' }; // reserved line — cards stay aligned
  return (
    <button
      type="button"
      className={`lc-card ${selected ? 'lc-selected' : ''} ${soldOut ? 'lc-out' : ''}`.trim().replace(/\s+/g, ' ')}
      onClick={() => onOpen(wine.id)}
      aria-haspopup="dialog"
    >
      <span>
        {wine.producer && <span className="lc-producer">{wine.producer}</span>}
        <span className="lc-name lc-serif">{wine.name}</span>
        {wine.classification && <span className="lc-class lc-serif">{wine.classification}</span>}
        <span className="lc-meta">
          <span className="lc-dot" style={{ background: dotVarFor(wine.color) }} />
          {metaBits}
        </span>
      </span>
      <span className="lc-card-right">
        <span className="lc-price">
          {wine.isFromPrice && <span className="lc-from">from </span>}
          {formatPrice(wine.minPrice, wine.currency)}
        </span>
        {wine.primaryFormat && (
          <span className="lc-size">
            {wine.primaryFormat.label}
            {wine.primaryFormat.sub ? ` · ${wine.primaryFormat.sub}` : ''}
          </span>
        )}
        <span className={status.className}>{status.text}</span>
      </span>
    </button>
  );
};

interface SectionProps {
  id: string;
  label: string;
  color: WineColorCanonical | 'OTHER' | null;
  wines: LaCarteWine[];
  selectedId: string | null;
  pickedIds: ReadonlySet<string>;
  onOpen: (id: string) => void;
  sectionRef?: (el: HTMLElement | null) => void;
}

/** A color section: serif heading, count, single-column stack of card boxes. */
const WineSection: React.FC<SectionProps> = ({
  id, label, wines, selectedId, pickedIds, onOpen, sectionRef,
}) => (
  <section className="lc-section" id={id} ref={sectionRef as React.Ref<HTMLElement>}>
    <div className="lc-section-head">
      <h3>{label}</h3>
      <span className="lc-count">{wines.length}</span>
    </div>
    <div className="lc-rows">
      {wines.map(w => (
        <WineCard
          key={w.id}
          wine={w}
          selected={selectedId === w.id}
          picked={pickedIds.has(w.id)}
          onOpen={onOpen}
        />
      ))}
    </div>
  </section>
);

export default WineSection;
